import { ArrowUpRight, Globe, Mail, Plus, Send } from "lucide-react";
import { useState, type FC } from "react";
import { toast } from "sonner";

import { ForeignNationalsAPI } from "@/api/foreignNationals";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";

import { FirmPortalAPI } from "../../../clientPortal/api";
import { clientPortalPreviewUrl } from "../../../clientPortal/preview";
import {
  refreshClientPortal,
  useFirmPortalState
} from "../../../clientPortal/queries";
import { SectionCard } from "../../components/SectionCard";
import {
  portalAccessView,
  portalLanguageNote,
  type PortalAccessView
} from "./portalAccessModel";

/** DOM id of the card, for deep links (`?portal=1`) and the header chip. */
export const PORTAL_ACCESS_DOM_ID = "client-portal-access";

const TONE: Record<PortalAccessView["tone"], StatusTone> = {
  off: "neutral",
  pending: "info",
  on: "success",
  warn: "warn"
};

type Props = {
  clientId: string;
  clientName: string;
  firstName: string;
  archived: boolean;
  preferredLanguage: string | null;
  /** Personal's Contact and Language section (email, preferred language). */
  onOpenPersonal: () => void;
  highlighted?: boolean;
};

/** Invite, resend, preview and turn off the client's portal. There is no
 * copyable link: a link only goes to the client's own inbox. */
export const PortalAccessCard: FC<Props> = ({
  clientId,
  clientName,
  firstName,
  archived,
  preferredLanguage,
  onOpenPersonal,
  highlighted = false
}) => {
  const state = useFirmPortalState(clientId);
  const [confirm, setConfirm] = useState<"invite" | "off" | null>(null);
  const [busy, setBusy] = useState(false);

  const invite = async () => {
    setBusy(true);
    try {
      const next = await FirmPortalAPI.invite(clientId);
      if (next.email_sent === false) {
        toast.error("Portal login is on, but the email didn't send", {
          description: "Try Resend in a minute."
        });
      } else {
        toast.success(`Invite sent to ${next.email}`, {
          description: "The link works once and expires in 7 days."
        });
      }
    } catch (e) {
      toast.error("Couldn't send the invite", {
        description: e instanceof Error ? e.message : undefined
      });
    } finally {
      setBusy(false);
      await refreshClientPortal();
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      await ForeignNationalsAPI.setAllowLogin(clientId, false);
      toast.success("Portal login is off", {
        description: "Open links stop working. Intake links still work."
      });
    } catch (e) {
      toast.error("Couldn't turn it off", {
        description: e instanceof Error ? e.message : undefined
      });
    } finally {
      setBusy(false);
      await refreshClientPortal();
    }
  };

  const s = state.data;
  const view = s ? portalAccessView(s, firstName) : null;
  const language = portalLanguageNote(preferredLanguage, firstName);

  return (
    <div id={PORTAL_ACCESS_DOM_ID}>
      <SectionCard
        title="Portal Access"
        className={
          highlighted
            ? "bg-accent-soft ring-accent-line transition-colors"
            : "transition-colors"
        }
        action={
          view ? (
            <StatusBadge
              tone={TONE[view.tone]}
              dot
              data-testid="portal-access-state"
            >
              {view.label}
            </StatusBadge>
          ) : null
        }
      >
        <div data-testid="portal-access-card" className="flex flex-col gap-3">
          {!s || !view ? (
            <Skeleton className="h-12 w-full" />
          ) : (
            <>
              <div className="flex min-w-0 flex-col gap-1">
                <span className="text-base text-fg-2">{view.description}</span>
                {s.email ? (
                  <span className="flex items-center gap-1.5 text-sm text-fg-3">
                    <Mail className="size-3.5" />
                    {s.email}
                  </span>
                ) : null}
                <span
                  className="flex items-start gap-1.5 text-sm text-fg-3"
                  data-testid="portal-language"
                >
                  <Globe className="mt-px size-3.5 shrink-0" />
                  <span>
                    {language.text}
                    {language.setInPersonal ? (
                      <>
                        {" Set a preferred language in "}
                        <button
                          type="button"
                          className="text-fg-2 underline underline-offset-2 hover:text-fg"
                          onClick={onOpenPersonal}
                          data-testid="portal-language-personal"
                        >
                          Personal
                        </button>
                        {" to change it."}
                      </>
                    ) : null}
                  </span>
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {view.action ? (
                  <Button
                    variant="primary"
                    disabled={busy || archived}
                    onClick={() =>
                      view.action?.kind === "invite"
                        ? setConfirm("invite")
                        : void invite()
                    }
                    data-testid="portal-invite"
                  >
                    <Send />
                    {view.action.label}
                  </Button>
                ) : null}
                {!s.email ? (
                  <Button
                    variant="primary"
                    onClick={onOpenPersonal}
                    data-testid="portal-add-email"
                  >
                    <Plus />
                    Add Email
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  disabled={archived}
                  onClick={() =>
                    window.open(
                      clientPortalPreviewUrl(clientId),
                      "_blank",
                      "noopener"
                    )
                  }
                  data-testid="portal-view-as-client"
                >
                  <ArrowUpRight />
                  View as Client
                </Button>
                {view.canTurnOff ? (
                  <Button
                    variant="ghost"
                    className="text-danger hover:text-danger"
                    disabled={busy}
                    onClick={() => setConfirm("off")}
                    data-testid="portal-turn-off"
                  >
                    Turn Off
                  </Button>
                ) : null}
              </div>
              {archived ? (
                <span className="text-sm text-fg-3">
                  Archived clients can&apos;t use the portal. Restore them to
                  invite them.
                </span>
              ) : null}
            </>
          )}
        </div>
      </SectionCard>

      <AlertDialog
        open={confirm !== null}
        onOpenChange={(open) => {
          if (!open) setConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "invite"
                ? `Invite ${clientName} to the portal?`
                : `Turn off portal login for ${clientName}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "invite"
                ? `We'll email ${
                    s?.email ?? "them"
                  } a sign-in link from your firm. They'll see their tasks, the stage of each case you share, and the documents you share with them.`
                : "They'll be signed out, and any sign-in links they have stop working. Intake links you've already sent keep working."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant={confirm === "off" ? "destructive" : "default"}
              onClick={() => {
                const next = confirm;
                setConfirm(null);
                if (next === "invite") void invite();
                else void turnOff();
              }}
            >
              {confirm === "invite" ? "Send Invite" : "Turn Off"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
